<template>
    <v-card color="surface">
        <v-toolbar flat dense color="primary" dark>
            <v-toolbar-title>
                Scheduling Sales
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
                <v-menu
                    offset-y
                    v-if="form.id">
                    <template v-slot:activator="{on, attrs}">
                        <v-btn
                            v-on="on"
                            v-bind="attrs" 
                            icon>
                            <v-icon>mdi-information</v-icon>
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title>
                            Information
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field
                                        label="Created By"
                                        v-model="form.created_by"
                                        disabled
                                        outlined
                                        hide-details="auto"
                                        dense>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" sm="12" md="6">
                                    <v-text-field
                                        label="Updated By"
                                        v-model="form.updated_by"
                                        disabled
                                        outlined
                                        hide-details="auto"
                                        dense>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-menu>
            </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="mt-4">
            <v-form
                ref="form">
                <v-row>
                    <v-col cols="6">
                        <PDatePicker
                            label="Date"
                            v-model="form.start_date"
                            outlined
                            :rules="[isRequired]"
                            hide/>
                    </v-col>
                    <v-col cols="12" md="6"  v-if="isEditMode">
                        <SchedulingStateSelect
                            label="State"
                            v-model="form.state_id"
                            item-text="name"
                            item-value="id"
                            :rules=[isRequired]
                            outlined
                            dense
                            hide-details="auto"
                            />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <CustomerSearch
                            label="Customer"
                            v-model="form.customer_id"
                            item-text="display_name"
                            :rules="[isRequired]"
                            :error-messages="errors.customer_id"
                            outlined
                            hide-details="auto"/>
                    </v-col>
                    <v-col cols="4" v-if="!!form.customer_id">
                        <v-btn block outlined @click="handleOpenCustomerCenter">Customer Center</v-btn>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="4">
                        <CustomerSitesSelect
                            label="Site"
                            outlined
                            dense
                            hide-details="auto"
                            :rules="[isRequired]"
                            item-text="name"
                            item-value="id"
                            :error-messages="errors.site_id"
                            v-model="form.site_id"
                            :customer-id="form.customer_id"
                            />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-select
                            label="Floor"
                            v-model="form.floor"
                            :items="floors"
                            outlined
                            dense
                            hide-details="auto"
                            >
                        </v-select>
                    </v-col>
                    <v-col cols="4">
                        <CastTypesSelect
                            label="Cast Type"
                            v-model="form.cast_type_id"
                            outlined
                            dense
                            item-text="name"
                            item-value="id"
                            :error-messages="errors.cast_type_id"
                            hide-details="auto"
                            />
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-autocomplete
                            label="Item"
                            :items="items"
                            v-model="form.item_id"
                            :rules="[isRequired]"
                            outlined
                            dense
                            hide-details="auto"
                            item-text="name"
                            item-value="id"
                            >
                        </v-autocomplete>
                    </v-col>
                    <v-col cols="3">
                        <v-text-field
                            label="Quantity"
                            v-model="form.quantity"
                            :rules="[isRequired, isNumeric]"
                            outlined
                            dense
                            hide-details="auto">
                        </v-text-field>
                    </v-col>
                    <v-col cols="3">
                        <v-select
                            label="Measure Unit"
                            :items="item.measure_units"
                            v-model="form.measure_unit_id"
                            :rules="[isRequired]"
                            outlined
                            dense
                            item-text="name"
                            item-value="id"
                            :error-messages="errors.measure_unit_id"
                            hide-details="auto"
                            >
                        </v-select>
                    </v-col>
                </v-row>
                 <v-row align="center">
                    <v-col cols="6">
                        <PumpsSelect
                            label="Pump"
                            v-model="form.pump_id"
                            item-text="name"
                            item-value="id"
                            :error-messages="errors.pump_id"
                            outlined
                            dense
                            clearable
                            hide-details="auto"
                            />
                    </v-col>
                    <v-col cols="3" v-if="showTime">
                        <PTimePicker
                            label="Start Time"
                            v-model="form.start_time"
                            :rules="[isRequired]"
                            outlined
                            dense
                            :error-messages="errors.start_at"
                            hide-details="auto"/>
                    </v-col>
                    <v-col cols="3">
                        <PTimePicker
                            label="End Time"
                            v-model="form.end_time"
                            :rules="[isRequired]"
                            outlined
                            dense
                            :error-messages="errors.end_at"
                            hide-details="auto"/>
                    </v-col>
                </v-row>
                <v-row align="center">
                     <v-col cols="12" sm="12" md="4">
                        <v-select
                            label="Plants"
                            :items="plants"
                            v-model="form.plant_id"
                            outlined
                            :loading="plants_loading"
                            dense
                            item-text="name"
                            item-value="id"
                            hide-details="auto">
                        </v-select>
                    </v-col>
                    <v-col cols="12" sm="12" md="4">
                        <v-select
                            label="Cubes"
                            :items="cubes"
                            v-model="form.cubes"
                            outlined
                            dense
                            hide-details="auto">
                        </v-select>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-textarea
                            outlined
                            v-model="form.remark"
                            label="Remarks"
                            hide-details>
                        </v-textarea>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-btn depressed color="primary" @click="handleSubmit">submit</v-btn>
            <v-btn text @click="handleClose">close</v-btn>
        </v-card-actions>
        <customer-display-dialog ref="customerDisplayDialog"> </customer-display-dialog>
        <PAlert v-model="alert.display" v-bind="alert"/>
        <v-overlay v-model="loading">
            <v-sheet color="white" class="pa-4" width="300" elevation="1" rounded>
                <v-progress-linear indeterminate color="primary"/>
                <div class="primary--text text-center mt-2">Please Wait ...</div>
            </v-sheet>
        </v-overlay>
    </v-card>
</template>

<script>
import moment from 'moment';
import { isRequired, isNumeric } from './../../utils/validations';
import { STATE_DRAFT } from './../../data/sales/scheduling/states'

import { createSalesSchedule, getSalesSchedule, updateSalesSchedule } from './../../services/scheduling/schedule';

import CustomerSearch from './../../components/searchComponents/CustomerSearch';
import PTimePicker from './../../components/common/PTimePicker';
import PDatePicker from './../../components/common/PDatePicker';
import CustomerDisplayDialog from './../Sales/CustomerDisplayDialog';
import CustomerSitesSelect from './../Sales/CustomerSitesSelect';
import PumpsSelect from './../Sales/PumpsSelect';
import SchedulingStateSelect from './../Sales/SchedulingStateSelect';
import PAlert from './../common/PAlert';
import CastTypesSelect from './CastTypesSelect';
import { getIdempotentKey } from './../../services/scheduling/index';
import { mapGetters } from 'vuex';

export default {
    name: "SchedulingEventEdit",
    mounted() {
        this.$store.dispatch('sales/plants/fetch');
    }, 
    components: {
        CustomerSearch,
        PTimePicker,
        PDatePicker,
        CustomerDisplayDialog,
        CustomerSitesSelect,
        PumpsSelect,
        SchedulingStateSelect,
        PAlert,
        CastTypesSelect
    },
    props: {
        date: String,
        eventId: [String, Number]
    },
    data() {
        return {
            errors: [],
            showTime: true,
            idempotentKey: null,
            form: {
                id: null,
                start_date: null,
                start_time: "",
                end_date: null,
                end_time: "",
                item_id: null,
                quantity: null,
                measure_unit_id: null,
                pump_id: null,
                remark: null,
                state_id: STATE_DRAFT,
                cast_type_id: null,
                cubes: 0,
                floor: null
            },
            isRequired,
            isNumeric,
            loading: false,
            alert: {
                display: false,
                title: null,
                content: null,
                callback: null
            },
            cubes: [
                { text: "No Lab", value: 0 },
                { text: "1 Set (6 Cubes)", value: 6 },
                { text: "2 Sets (12 Cubes)", value: 12 },
                { text: "3 Sets (18 Cubes)", value: 18 },
                { text: "4 Sets (24 Cubes)", value: 24 },
                { text: "5 Sets (30 Cubes)", value: 30 },
                { text: "6 Sets (36 Cubes)", value: 36 },
            ],
            floors: [
                { text: "Basement", value: -1 },
                { text: "Ground Floor", value: 0 },
                { text: "1st Floor", value: 1 },
                { text: "2nd Floor", value: 2 },
                { text: "3rd Floor", value: 3 },
                { text: "4th Floor", value: 4 },
                { text: "5th Floor", value: 5 },
                { text: "6th Floor", value: 6 },
                { text: "7th Floor", value: 7 },
                { text: "8th Floor", value: 8 },
                { text: "9th Floor", value: 9 },
                { text: "10th Floor", value: 10 },
                { text: "11th Floor", value: 11 },
                { text: "12th Floor", value: 12 },
                { text: "13th Floor", value: 13 },
                { text: "14th Floor", value: 14 },
                { text: "15th Floor", value: 15 },
                { text: "16th Floor", value: 16 },
                { text: "17th Floor", value: 17 },
                { text: "18th Floor", value: 18 },
                { text: "19th Floor", value: 19 },
                { text: "20th Floor", value: 20 },
            ],

        }
    },
    computed: {
        ...mapGetters({
            items: "scheduling/item/items"
        }),
        isEditMode() {
            return !!this.eventId;
        },
        plants() {
            return this.$store.getters['sales/plants/plants']
        },
        plants_loading () {
            return this.$store.getters['sales/plants/loading']
        },
        item() {
            if(!this.form.item_id) return {};
            return this.items.find(each => each.id == this.form.item_id);
        }
    },
    methods: {
        handleClose() {
            this.$emit("close");
        },
        async fetchSalesSchedule() {
            try {
                this.loading = true;
                const resp = await getSalesSchedule(this.eventId);
                const d = resp.data;
    
                this.form = {
                    ...d,
                    start_date: this.extractDate(d.start_at),
                    start_time: this.extractTime(d.start_at),
                    end_date: this.extractDate(d.end_at),
                    end_time: this.extractTime(d.end_at),
                };
                
                this.loading = false;
            } catch (error) {
                this.loading = false;
                let message = error.response ? error.response.data.message : error;
                this.$emit("error", message);
                this.$emit("fatal");
            }
        },
        extractDate(datetime) {
            return moment(datetime).format("YYYY-MM-DD");
        },
        extractTime(datetime) {
            return moment(datetime).format("HH:mm");
        },
        implodeDatetime(date, time) {
            return `${date} ${time}`
        },
        init() {
            if(this.isEditMode) this.fetchSalesSchedule();
        },
        handleSubmit() {
            this.alert = {
                title: "Please Confirm",
                content: "Are you sure you want to save?",
                callback: this.submit,
                display: true
            }
        },
        async submit() {
            if(!this.$refs.form.validate()) return;

            this.loading = true;

            if(!this.idempotentKey) {
                let ikResp = await getIdempotentKey();
                this.idempotentKey = ikResp.data.key;
            }

            this.form.end_date = this.form.start_date;
            let data = { 
                ...this.form,
                idempotentKey: this.idempotentKey,
                start_at: this.implodeDatetime(this.form.start_date, this.form.start_time),
                end_at: this.implodeDatetime(this.form.end_date, this.form.end_time),
            }

            const func = this.isEditMode ? updateSalesSchedule : createSalesSchedule;

            func(data)
                .then(resp => {
                    this.$emit("success", resp.data.message);
                    this.$emit("record", resp.data.record);
                })
                .catch(error => {
                    this.$emit("error", error);
                })
                .finally(() => this.loading = false)
        },
        handleOpenCustomerCenter() {
            this.$refs.customerDisplayDialog.open({ id: this.form.customer_id });
        }
    },
    created() {
        this.init();
        this.form.start_date = this.date;
        this.form.end_date = this.date;
    }
}
</script>

<style>

</style>